import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { AlertTriangle, CalendarDays, ChevronDown, ChevronRight, Download, FileSpreadsheet, MapPin, RefreshCw, ShieldAlert, Users } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const SEARCH_STATE_BADGE = {
  no_invites: { label: "Dep search not started", className: "border-red-500/40 text-red-300 bg-red-500/10" },
  pending: { label: "Pending dep replies", className: "border-amber-500/40 text-amber-300 bg-amber-500/10" },
  all_declined: { label: "All deps declined", className: "border-rose-500/40 text-rose-300 bg-rose-500/10" },
  covered: { label: "Dep found", className: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" },
};

const RSVP_BADGE = {
  "Can't go": "border-red-500/40 text-red-300 bg-red-500/10",
  "Maybe": "border-amber-500/40 text-amber-300 bg-amber-500/10",
  "No response": "border-slate-500/40 text-slate-300 bg-slate-500/10",
};

const downloadBlob = async (url, filename) => {
  try {
    const res = await apiClient.get(url, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    const objectUrl = window.URL.createObjectURL(blob);
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (e) {
    toast.error(`Export failed: ${e?.response?.data?.detail || e.message}`);
  }
};

const MissingPlayers = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter-98: each concert card collapses by default to keep the page tidy
  // for bands with lots of upcoming gigs. Tracks per-event open state.
  const [openIds, setOpenIds] = useState({});
  const toggleOpen = (id) => setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/reports/missing-players");
      setData(res.data);
    } catch (e) {
      toast.error(`Failed to load report: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const concerts = data?.concerts || [];
  const totalMissing = concerts.reduce((sum, c) => sum + (c.missing_count || 0), 0);
  const concertsWithGaps = concerts.filter((c) => c.missing_count > 0).length;

  return (
    <div className="space-y-6" data-testid="missing-players-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-primary" />
            Missing Players
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Upcoming concerts where a seat isn't yet covered — a member said no / maybe / hasn't replied,
            and no dep has accepted in their place.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReport}
            disabled={loading}
            data-testid="missing-players-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadBlob("/reports/missing-players.pdf", "missing-players.pdf")}
            data-testid="missing-players-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadBlob("/reports/missing-players.xlsx", "missing-players.xlsx")}
            data-testid="missing-players-xlsx-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="glass-card" data-testid="stat-upcoming-concerts">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Upcoming concerts</p>
            <p className="text-3xl font-heading mt-1">{concerts.length}</p>
          </CardContent>
        </Card>
        <Card className="glass-card" data-testid="stat-concerts-with-gaps">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">With gaps</p>
            <p className="text-3xl font-heading mt-1 text-amber-400">{concertsWithGaps}</p>
          </CardContent>
        </Card>
        <Card className="glass-card" data-testid="stat-total-missing">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Uncovered seats</p>
            <p className="text-3xl font-heading mt-1 text-red-400">{totalMissing}</p>
          </CardContent>
        </Card>
      </div>

      {loading && !data ? (
        <Card className="glass-card">
          <CardContent className="p-10 text-center text-muted-foreground">Loading…</CardContent>
        </Card>
      ) : concerts.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-10 text-center text-muted-foreground" data-testid="no-concerts-empty">
            <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-50" />
            No upcoming concerts on the schedule.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {concerts.map((c) => {
            const isOpen = openIds[c.event_id] ?? (c.missing_count > 0);
            const safeTitle = (c.title || "concert").replace(/[^A-Za-z0-9]+/g, "-").toLowerCase();
            return (
            <Card
              key={c.event_id}
              className={`glass-card border-l-4 ${c.missing_count > 0 ? "border-l-red-500/70" : "border-l-emerald-500/70"}`}
              data-testid={`concert-card-${c.event_id}`}
            >
              <CardHeader className="border-b border-white/5 pb-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => toggleOpen(c.event_id)}
                    className="flex items-start gap-2 text-left min-w-0 flex-1 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/40 rounded-md -m-1 p-1"
                    data-testid={`concert-toggle-${c.event_id}`}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? (
                      <ChevronDown className="w-4 h-4 mt-1 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 mt-1 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0">
                      <CardTitle className="font-heading text-lg">{c.title}</CardTitle>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3.5 h-3.5" />
                          {c.start_time ? format(new Date(c.start_time), "EEE d MMM yyyy 'at' HH:mm") : "TBC"}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {c.location}
                        </span>
                        {c.status && c.status !== "confirmed" && (
                          <Badge variant="outline" className="text-xs capitalize">{c.status}</Badge>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      <span className={c.missing_count > 0 ? "text-red-400 font-medium" : "text-emerald-400 font-medium"}>
                        {c.covered_count}
                      </span>
                      {" / "}{c.total_members} covered
                    </span>
                    {c.missing_count > 0 && (
                      <Badge variant="outline" className="border-red-500/40 text-red-300 bg-red-500/10" data-testid={`missing-count-${c.event_id}`}>
                        {c.missing_count} missing
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={(e) => { e.stopPropagation(); downloadBlob(`/reports/missing-players.pdf?event_id=${encodeURIComponent(c.event_id)}`, `${safeTitle}-missing-players.pdf`); }}
                      data-testid={`event-export-pdf-${c.event_id}`}
                      title="Export this concert as PDF"
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      PDF
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2"
                      onClick={(e) => { e.stopPropagation(); downloadBlob(`/reports/missing-players.xlsx?event_id=${encodeURIComponent(c.event_id)}`, `${safeTitle}-missing-players.xlsx`); }}
                      data-testid={`event-export-xlsx-${c.event_id}`}
                      title="Export this concert as Excel"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
                      Excel
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {isOpen && (
              <CardContent className="p-4">
                {c.missing_players.length === 0 && (c.vacant_seats || []).length === 0 ? (
                  <p className="text-sm text-emerald-400 flex items-center gap-2" data-testid={`covered-${c.event_id}`}>
                    <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                    All seats covered for this concert.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {c.missing_players.map((mp) => {
                      const isCovered = mp.dep_search_state === "covered";
                      const searchBadge = SEARCH_STATE_BADGE[mp.dep_search_state] || SEARCH_STATE_BADGE.no_invites;
                      const rsvpClass = RSVP_BADGE[mp.rsvp_label] || RSVP_BADGE["No response"];
                      return (
                        <div
                          key={mp.player_id}
                          className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 p-3 rounded-lg transition-colors ${
                            isCovered
                              ? "bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/25"
                              : "bg-secondary/20 hover:bg-secondary/30"
                          }`}
                          data-testid={`missing-row-${c.event_id}-${mp.player_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium break-words">{mp.name}</div>
                            <div className="text-xs text-muted-foreground mt-0.5 break-words">
                              {(mp.section_names || []).join(", ") || "Unassigned"}
                            </div>
                            {isCovered && mp.covered_dep_name && (
                              <div
                                className="text-xs text-emerald-300 font-medium mt-1 break-words flex items-center gap-1"
                                data-testid={`covered-dep-${mp.player_id}`}
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                                Dep found — {mp.covered_dep_name}
                              </div>
                            )}
                            {!isCovered && mp.pending_dep_name && (
                              <div className="text-xs text-amber-400/90 italic mt-1 break-words" data-testid={`pending-dep-${mp.player_id}`}>
                                Waiting on {mp.pending_dep_name}…
                              </div>
                            )}
                            {mp.player_notes && (
                              <div className="text-xs text-muted-foreground italic mt-1 break-words">"{mp.player_notes}"</div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1.5 sm:shrink-0 sm:justify-end">
                            <Badge variant="outline" className={`text-xs ${rsvpClass}`}>
                              {mp.rsvp_label}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${searchBadge.className}`}>
                              {isCovered ? (
                                <span className="w-3 h-3 mr-1 inline-flex items-center justify-center">✓</span>
                              ) : (
                                <AlertTriangle className="w-3 h-3 mr-1" />
                              )}
                              {searchBadge.label}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* iter-149c: Vacant seats — section+part with no permanent
                    member assigned OR nobody attending, exposed inline so
                    admins see structural gaps alongside missing members. */}
                {(c.vacant_seats || []).length > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/[0.06]" data-testid={`vacant-seats-${c.event_id}`}>
                    <p className="text-[11px] uppercase tracking-wider text-amber-400/80 font-semibold mb-2">
                      Vacant seats ({(c.vacant_seats || []).length})
                    </p>
                    <div className="space-y-2">
                      {c.vacant_seats.map((v, idx) => {
                        const covered = !!v.dep_cover;
                        return (
                          <div
                            key={`${v.section_id}-${v.part}-${idx}`}
                            className={`flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 p-3 rounded-lg transition-colors ${
                              covered
                                ? "bg-emerald-500/10 border border-emerald-500/25"
                                : "bg-amber-500/5 border border-amber-500/20"
                            }`}
                            data-testid={`vacant-row-${c.event_id}-${idx}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium break-words">
                                {v.section_name} · <span className="text-muted-foreground">{v.part}</span>
                              </div>
                              <div className="text-xs text-muted-foreground mt-0.5 break-words">
                                {(v.regular_members || []).length > 0
                                  ? `Usually: ${(v.regular_members || []).join(", ")}`
                                  : "No permanently-assigned member"}
                              </div>
                              {covered && (
                                <div className="text-xs text-emerald-300 font-medium mt-1 break-words flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                                  Dep found — {v.dep_cover.dep_name}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-1.5 sm:shrink-0 sm:justify-end">
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  covered
                                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                                    : "border-amber-500/40 text-amber-300 bg-amber-500/10"
                                }`}
                              >
                                {covered ? "Dep found" : "Vacant seat"}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
              )}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MissingPlayers;
